import { useState } from 'react'

import styles from './GlobalPropertiesPanel.module.scss'
import sharedStyles from '@/pages/app/AnalyticsPortal/styles.module.scss'

import Button from '@/components/Button'
import DialogWrapper from '@/components/Dialog'
import Input from '@/components/Input'
import { STATUS_CLASS_MAP } from '@/pages/app/AnalyticsPortal/constants'
import type { AnalyticsEvent, EditingProperty, GlobalProperty } from '@/pages/app/AnalyticsPortal/types'

type GlobalPropertiesSchema = Record<string, GlobalProperty>

interface GlobalPropertiesPanelProps {
  globalPropertiesEvent: AnalyticsEvent | null
  isLoading: boolean
  pendingChanges: GlobalPropertiesSchema | null
  onPendingChangesUpdate: (changes: GlobalPropertiesSchema | null) => void
}

const getSchema = (
  pendingChanges: GlobalPropertiesSchema | null,
  globalPropertiesEvent: AnalyticsEvent | null
): GlobalPropertiesSchema => {
  return (pendingChanges ?? globalPropertiesEvent?.properties_schema ?? {}) as GlobalPropertiesSchema
}

const GlobalPropertiesPanel = ({
  globalPropertiesEvent,
  isLoading,
  pendingChanges,
  onPendingChangesUpdate,
}: GlobalPropertiesPanelProps) => {
  const [editing, setEditing] = useState<EditingProperty | null>(null)
  const [showEditor, setShowEditor] = useState(false)
  const [formName, setFormName] = useState('')
  const [formDesc, setFormDesc] = useState('')

  const schema = getSchema(pendingChanges, globalPropertiesEvent)
  const properties = Object.entries(schema).map(([name, prop]) => ({
    name,
    description: prop.description || '',
    lifecycle_state: prop.lifecycle_state ?? 'draft',
    status: prop.status,
  }))

  const openEditor = (prop: EditingProperty | null) => {
    setEditing(prop)
    setFormName(prop?.name ?? '')
    setFormDesc(prop?.description ?? '')
    setShowEditor(true)
  }

  const closeEditor = () => {
    setShowEditor(false)
    setEditing(null)
  }

  const handleSave = () => {
    if (!formName.trim() || !formDesc.trim()) return
    const currentSchema = getSchema(pendingChanges, globalPropertiesEvent)
    const updatedSchema = { ...currentSchema }
    if (editing && editing.name !== formName.trim()) {
      delete updatedSchema[editing.name]
    }
    updatedSchema[formName.trim()] = {
      description: formDesc,
      lifecycle_state: editing?.lifecycle_state ?? 'draft',
      status: 'needs_publish',
    }
    onPendingChangesUpdate(updatedSchema)
    closeEditor()
  }

  const handleDeprecate = (name: string) => {
    const currentSchema = getSchema(pendingChanges, globalPropertiesEvent)
    onPendingChangesUpdate({
      ...currentSchema,
      [name]: { ...currentSchema[name], lifecycle_state: 'deprecated', status: 'needs_publish' },
    })
  }

  const handleUndeprecate = (name: string) => {
    const currentSchema = getSchema(pendingChanges, globalPropertiesEvent)
    const originalSchema = (globalPropertiesEvent?.properties_schema as Record<string, GlobalProperty>) ?? {}
    const originalLifecycle = originalSchema[name]?.lifecycle_state ?? 'active'
    onPendingChangesUpdate({
      ...currentSchema,
      [name]: { ...currentSchema[name], lifecycle_state: originalLifecycle, status: 'needs_publish' },
    })
  }

  const getOriginalLifecycle = (name: string) => {
    const originalSchema = (globalPropertiesEvent?.properties_schema as Record<string, GlobalProperty>) ?? {}
    return originalSchema[name]?.lifecycle_state ?? 'active'
  }

  const existingNames = properties.map((p) => p.name).filter((n) => n !== editing?.name)
  const nameError = existingNames.includes(formName.trim()) ? 'Name already exists' : null
  const isFormValid = !nameError && formName.trim() && formDesc.trim()

  if (isLoading) {
    return (
      <div className={sharedStyles.tableContainer}>
        <div className={sharedStyles.loadingState}>Loading global properties...</div>
      </div>
    )
  }

  return (
    <div className={sharedStyles.tableContainer}>
      <div className={sharedStyles.tableControls}>
        <div className={styles.globalPropertiesTitleSection}>
          <span className={styles.globalPropertiesTitle}>Global Event Properties</span>
          <span className={styles.globalPropertiesSubtitle}>Included with every event</span>
        </div>
      </div>

      {properties.length === 0 ? (
        <div className={sharedStyles.emptyState}>No global properties defined.</div>
      ) : (
        <div className={sharedStyles.tableWrapper}>
          <table className={sharedStyles.table}>
            <thead className={sharedStyles.tableHeader}>
              <tr>
                <th>Name</th>
                <th>Description</th>
                <th>Lifecycle</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody className={sharedStyles.tableBody}>
              {properties.map((prop) => {
                const originalLifecycle = getOriginalLifecycle(prop.name)
                const isOriginallyDeprecated = originalLifecycle === 'deprecated'
                const isDeprecatedByPending = prop.lifecycle_state === 'deprecated' && !isOriginallyDeprecated

                return (
                  <tr key={prop.name} className={isOriginallyDeprecated ? sharedStyles.deprecatedRow : undefined}>
                    <td>
                      <span className={sharedStyles.truncatedText}>{prop.name}</span>
                    </td>
                    <td>
                      <span className={sharedStyles.truncatedText} title={prop.description}>
                        {prop.description || '-'}
                      </span>
                    </td>
                    <td>
                      <span className={`${sharedStyles.lifecycleBadge} ${sharedStyles[prop.lifecycle_state]}`}>
                        {prop.lifecycle_state}
                      </span>
                    </td>
                    <td>
                      <span
                        className={`${sharedStyles.statusBadge} ${sharedStyles[STATUS_CLASS_MAP[prop.status ?? 'needs_publish']]}`}
                      >
                        {prop.status?.replace(/_/g, ' ') || 'needs publish'}
                      </span>
                    </td>
                    <td>
                      <div className={sharedStyles.actionButtons}>
                        <button className={sharedStyles.actionButton} onClick={() => openEditor(prop)}>
                          Edit
                        </button>
                        {!isOriginallyDeprecated &&
                          (isDeprecatedByPending ? (
                            <button
                              className={`${sharedStyles.actionButton} ${sharedStyles.undeprecate}`}
                              onClick={() => handleUndeprecate(prop.name)}
                            >
                              Undeprecate
                            </button>
                          ) : (
                            <button
                              className={`${sharedStyles.actionButton} ${sharedStyles.deprecate}`}
                              onClick={() => handleDeprecate(prop.name)}
                            >
                              Deprecate
                            </button>
                          ))}
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {showEditor && (
        <DialogWrapper
          isOpen={true}
          title={editing ? 'Edit Property' : 'Add Property'}
          close={closeEditor}
          minWidth={520}
        >
          <div className={sharedStyles.dialogBody}>
            <div className={sharedStyles.formGroup}>
              <label className={sharedStyles.label}>Name *</label>
              <Input type="text" value={formName} onInput={setFormName} placeholder="e.g. sessionId" />
              {nameError && <span className={sharedStyles.errorText}>{nameError}</span>}
            </div>
            <div className={sharedStyles.formGroup}>
              <label className={sharedStyles.label}>Description *</label>
              <textarea
                className={sharedStyles.textarea}
                value={formDesc}
                onChange={(e) => setFormDesc(e.target.value)}
                placeholder="Describe what this property represents..."
              />
            </div>
            <div className={sharedStyles.dialogFooter}>
              <Button title="Cancel" onClick={closeEditor} type="light" />
              <Button title={editing ? 'Save' : 'Add'} onClick={handleSave} type="dark" isDisabled={!isFormValid} />
            </div>
          </div>
        </DialogWrapper>
      )}
    </div>
  )
}

export default GlobalPropertiesPanel
